import { Time } from "./Timer";

export const PAUSED_KEY_PREFIX = "paused_";

export interface SavedTimerState {
  secondsRemaining: number;
  totalSessionTime: number;
  initialMinutes: number;
  originalMinutes: number;
  savedAt: number;
  /** Paused timers don't count down while away. Missing on older saves. */
  isPaused?: boolean;
}

export type RestoreResult =
  | { status: "none" }
  | { status: "alarm"; partialTime: Partial<Time> }
  | { status: "restored"; partialTime: Partial<Time> };

/** Returns the localStorage key for a given session name. */
export const getPausedKey = (name: string): string =>
  `${PAUSED_KEY_PREFIX}${name}`;

/**
 * Builds the object to persist to localStorage.
 * Pure — no side effects.
 */
export const buildSavedState = ({
  time,
  isPaused,
  now = Date.now(),
}: {
  time: Time;
  isPaused: boolean;
  now?: number;
}): SavedTimerState => ({
  secondsRemaining: time.secondsRemaining,
  totalSessionTime: time.totalSessionTime,
  initialMinutes: time.initialMinutes,
  originalMinutes: time.originalMinutes,
  savedAt: now,
  isPaused,
});

/** True if a save was made on an earlier calendar day than `now`. */
export const isStaleSave = (
  saved: SavedTimerState,
  now: number = Date.now()
): boolean =>
  new Date(saved.savedAt).toDateString() !== new Date(now).toDateString();

/**
 * Determines what to do with a saved timer state given the current time.
 * Pure — reads the saved object, returns a result, touches nothing.
 *
 * - "none"     → no saved state found, or it's from a previous day (stale)
 * - "alarm"    → time ran out while away; caller should fire alarm
 * - "restored" → valid remaining time; caller should restore paused UI
 */
export const resolveRestoreResult = (
  saved: SavedTimerState | null,
  now: number = Date.now()
): RestoreResult => {
  if (!saved) return { status: "none" };
  // Stale saves are submitted on app load by pushOldFlowmodoros (useRecords)
  if (isStaleSave(saved, now)) return { status: "none" };

  const { secondsRemaining, totalSessionTime, initialMinutes, savedAt } = saved;
  const originalMinutes = saved.originalMinutes ?? initialMinutes;
  // A paused timer resumes exactly where it stopped
  const elapsedSeconds = saved.isPaused ? 0 : Math.floor((now - savedAt) / 1000);
  const correctedSeconds = secondsRemaining - elapsedSeconds;

  const partialTime: Partial<Time> = {
    initialMinutes,
    originalMinutes,
    totalSessionTime,
  };

  if (correctedSeconds <= 0) {
    return {
      status: "alarm",
      partialTime: { ...partialTime, secondsRemaining: 0 },
    };
  }

  return {
    status: "restored",
    partialTime: { ...partialTime, secondsRemaining: correctedSeconds },
  };
};

/**
 * Pure state transition for one worker tick.
 * Using ...prev spread ensures no field is ever accidentally dropped.
 */
export const computeNextTimerState = (
  prev: Time,
  workerSecondsRemaining: number
): Time => {
  if (prev.secondsRemaining === 1) {
    return { ...prev, secondsRemaining: 0, totalSessionTime: prev.totalSessionTime + 1 };
  }
  if (
    prev.initialMinutes * 60 !== prev.secondsRemaining &&
    prev.secondsRemaining % 60 === 0
  ) {
    return { ...prev, secondsRemaining: workerSecondsRemaining, totalSessionTime: prev.totalSessionTime + 1 };
  }
  return { ...prev, secondsRemaining: workerSecondsRemaining };
};

/**
 * Parses a raw localStorage value for a paused session.
 * Returns null if missing or malformed.
 */
export const parseSavedState = (raw: string | null): SavedTimerState | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SavedTimerState;
  } catch {
    return null;
  }
};
